(() => {
  const DAY = 86400000;
  const iso = date => date.toISOString().slice(0, 10);
  const shift = (date, days) => new Date(date.getTime() + days * DAY);
  const holidayCache = new Map();
  function holidays(year) {
    if (holidayCache.has(year)) return holidayCache.get(year);
    const a = year % 19, b = Math.floor(year / 100), c = year % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = (h + l - 7 * m + 114) % 31 + 1;
    const easter = new Date(Date.UTC(year, month - 1, day));
    const dates = new Set(['01-01', '03-15', '05-01', '08-20', '10-23', '11-01', '12-25', '12-26'].map(date => `${year}-${date}`));
    [-2, 1, 50].forEach(offset => dates.add(iso(shift(easter, offset))));
    holidayCache.set(year, dates);
    return dates;
  }
  function working(date, closures = '') {
    return date.getUTCDay() !== 0 && date.getUTCDay() !== 6 &&
      !holidays(date.getUTCFullYear()).has(iso(date)) &&
      !closures.split(/[\s,;]+/).includes(iso(date));
  }
  function addWorking(date, count, closures) {
    let result = new Date(date);
    for (let remaining = Math.max(0, count), limit = 0; remaining && limit < 1500; limit++) {
      result = shift(result, 1);
      if (working(result, closures)) remaining--;
      if (limit === 1499) throw new Error('Invalid delivery calendar');
    }
    return result;
  }
  function startDay(now, cutoff = 12, closures = '') {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Budapest', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', hourCycle: 'h23'
    }).formatToParts(now).map(part => [part.type, part.value]));
    let date = new Date(`${parts.year}-${parts.month}-${parts.day}T00:00:00Z`);
    if (!working(date, closures) || Number(parts.hour) >= cutoff) date = addWorking(date, 1, closures);
    return date;
  }
  function groupItems(items, orderMin = 5, orderMax = 10) {
    const groups = {}, totals = new Map();
    items.forEach(item => totals.set(item.id, (totals.get(item.id) || 0) + Number(item.quantity)));
    let unavailable = false;
    items.filter(item => item.physical && Number(item.quantity) > 0).forEach(item => {
      const shortage = item.tracked && Number(item.stock) < totals.get(item.id);
      if (!item.available || (shortage && !item.continue)) { unavailable = true; return; }
      const type = shortage ? 'backorder' : 'in';
      const min = shortage ? Number(orderMin) : Number(item.min);
      const max = shortage ? Number(orderMax) : Number(item.max);
      const group = groups[type] ||= { type, min: 1, max: 1, titles: [] };
      group.min = Math.max(group.min, min);
      group.max = Math.max(group.max, max, group.min);
      if (!group.titles.includes(item.title)) group.titles.push(item.title);
    });
    return { groups: ['in', 'backorder'].filter(type => groups[type]).map(type => groups[type]), unavailable };
  }
  function calculate(config, now = new Date()) {
    const first = startDay(now, Number(config.cutoff), config.closures);
    const zone = config.zones.find(zone => zone.countries.split(',').map(c => c.trim()).includes(config.country));
    const result = groupItems(config.items, config.orderMin, config.orderMax);
    result.groups.forEach(group => {
      group.readyMin = addWorking(first, group.min - 1, config.closures);
      group.readyMax = addWorking(first, group.max - 1, config.closures);
      if (zone) {
        group.transitMin = Math.max(1, Number(zone.min));
        group.transitMax = Math.max(group.transitMin, Number(zone.max));
        group.deliveryMin = addWorking(group.readyMin, group.transitMin, config.closures);
        group.deliveryMax = addWorking(group.readyMax, group.transitMax, config.closures);
      }
    });
    return result;
  }
  globalThis.PeriplusDeliveryCalendar = { holidays, working, addWorking, startDay, groupItems, calculate, iso };
  if (typeof document === 'undefined') return;
  const node = (tag, text, className) => {
    const el = document.createElement(tag);
    if (text !== undefined) el.textContent = text;
    if (className) el.className = className;
    return el;
  };
  class PeriplusStockCount extends HTMLElement {
    connectedCallback() { this.textContent = new Intl.NumberFormat(document.documentElement.lang).format(Number(this.dataset.count)); }
  }
  if (!customElements.get('periplus-stock-count')) customElements.define('periplus-stock-count', PeriplusStockCount);
  let miniCartExpanded = true;
  class PeriplusDelivery extends HTMLElement {
    connectedCallback() {
      const input = this.querySelector('[data-delivery-config]');
      if (!input) return;
      try { this.config = JSON.parse(input.textContent); } catch { return; }
      this.output = this.querySelector('[data-delivery-output]');
      if (this.hasAttribute('data-collapsible')) {
        const details = node('details');
        details.open = miniCartExpanded;
        details.append(node('summary', this.config.text.heading, 'pp-delivery-summary'));
        this.output.replaceChildren(details);
        this.output = node('div', undefined, 'pp-delivery-body');
        details.append(this.output);
        details.addEventListener('toggle', () => { if (details.isConnected) miniCartExpanded = details.open; });
      }
      this.form = this.closest('form');
      this.refresh = () => this.render();
      this.form?.addEventListener('input', this.refresh);
      this.form?.addEventListener('change', this.refresh);
      this.timer = setInterval(this.refresh, 30000);
      this.render();
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.form?.removeEventListener('input', this.refresh);
      this.form?.removeEventListener('change', this.refresh);
    }
    render() {
      const config = structuredClone(this.config), text = config.text;
      if (this.hasAttribute('data-product')) {
        const quantity = this.form?.elements.namedItem('quantity')?.value;
        config.items[0].quantity = Number(quantity || 1);
      }
      const { groups, unavailable } = calculate(config);
      const output = this.output;
      const fragment = document.createDocumentFragment();
      const range = (min, max) => min === max ? String(min) : `${min}–${max}`;
      const days = (min, max) => min === 1 && max === 1 ? text.oneDay : text.days.replace('[days]', range(min, max));
      const dates = (min, max) => {
        const includeYear = min.getUTCFullYear() !== max.getUTCFullYear() || min.getUTCFullYear() !== startDay(new Date()).getUTCFullYear();
        const formatter = new Intl.DateTimeFormat(config.locale, { ...(includeYear ? {year:'numeric'} : {}), month:'short', day:'numeric', timeZone:'UTC' });
        return iso(min) === iso(max) ? formatter.format(min) : formatter.formatRange(min, max);
      };
      if (groups.length) {
        if (!this.hasAttribute('data-collapsible')) fragment.append(node('strong', text.heading, 'pp-delivery-heading'));
        const destination = node('p', `${text.destination}: `, 'pp-delivery-destination');
        if (config.countryFlag) {
          const flag = node('img');
          flag.src = config.countryFlag;
          flag.alt = '';
          flag.width = 20;
          flag.height = 15;
          destination.append(flag);
        }
        destination.append(node('span', config.countryName));
        fragment.append(destination);
      }
      groups.forEach(group => {
        const section = node('section', undefined, 'pp-delivery-group');
        const badge = node('strong', undefined, `badge-stock ${group.type === 'backorder' ? 'badge-stock-preorder badge-preorder' : 'badge-stock-in'}`);
        const dot = node('span', undefined, 'badge-stock-dot');
        dot.setAttribute('aria-hidden', 'true');
        badge.append(dot, node('span', text[group.type]));
        section.append(badge);
        const list = node('dl');
        const row = (label, value, prominent = false) => {
          const wrapper = node('div', undefined, prominent ? 'pp-delivery-date' : undefined);
          wrapper.append(node('dt', label), node('dd', value));
          list.append(wrapper);
        };
        if (group.deliveryMin) row(text.delivery, dates(group.deliveryMin, group.deliveryMax), true);
        row(text.pickup, dates(group.readyMin, group.readyMax), true);
        row(text.preparation, days(group.min, group.max));
        row(text.shipping, group.deliveryMin ? days(group.transitMin, group.transitMax) : text.unknown);
        section.append(list);
        fragment.append(section);
      });
      if (unavailable) fragment.append(node('p', text.out, 'pp-delivery-note'));
      if (groups.length > 1) fragment.append(node('p', text.split, 'pp-delivery-note'));
      if (groups.length) {
        fragment.append(node('p', text.pickupNote, 'pp-delivery-note'));
        fragment.append(node('p', text.cutoff.replace('[time]', `${String(config.cutoff).padStart(2, '0')}:00`), 'pp-delivery-note'));
        fragment.append(node('p', text.estimate, 'pp-delivery-note'));
        fragment.append(node('p', text.payment, 'pp-delivery-note'));
      }
      const next = node('div');
      next.append(fragment);
      if (output.innerHTML !== next.innerHTML) output.replaceChildren(...next.childNodes);
      this.hidden = !groups.length && !unavailable;
    }
  }
  if (!customElements.get('periplus-delivery')) customElements.define('periplus-delivery', PeriplusDelivery);
})();
